import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Loader2,
  Phone,
  AlertCircle,
  CheckCircle,
  ExternalLink,
} from "lucide-react";
import { VoiceAgent } from "@/services/voiceAgentService";
import { usePhoneNumberMutations } from "@/hooks/voice/usePhoneNumberMutations";

interface TwilioPhoneNumberConnectProps {
  voiceAgent: VoiceAgent;
  onSuccess: (updatedAgent: VoiceAgent) => void;
}

export function TwilioPhoneNumberConnect({
  voiceAgent,
  onSuccess,
}: TwilioPhoneNumberConnectProps) {
  const { connectPhone, isLoading } = usePhoneNumberMutations();

  // Check if agent has stored credentials but not connected to VAPI (reconnect scenario)
  const hasStoredCredentials =
    voiceAgent.phone_number &&
    voiceAgent.twilio_account_sid &&
    voiceAgent.twilio_auth_token;
  const isReconnectScenario =
    hasStoredCredentials && !voiceAgent.vapi_phone_number_id;

  const [formData, setFormData] = useState({
    phoneNumber: voiceAgent.phone_number || "",
    twilioAccountSid: voiceAgent.twilio_account_sid || "",
    twilioAuthToken: voiceAgent.twilio_auth_token || "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\+[1-9]\d{1,14}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber =
        "Please enter a valid phone number in E.164 format (e.g., +1234567890)";
    }

    if (!formData.twilioAccountSid.trim()) {
      newErrors.twilioAccountSid = "Twilio Account SID is required";
    } else if (!/^AC[a-fA-F0-9]{32}$/.test(formData.twilioAccountSid)) {
      newErrors.twilioAccountSid =
        "Invalid Account SID format (should start with AC)";
    }

    if (!formData.twilioAuthToken.trim()) {
      newErrors.twilioAuthToken = "Twilio Auth Token is required";
    } else if (formData.twilioAuthToken.length < 32) {
      newErrors.twilioAuthToken = "Auth Token appears to be too short";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // Check if all fields are filled (for determining button state)
  const allFieldsFilled =
    formData.phoneNumber.trim() &&
    formData.twilioAccountSid.trim() &&
    formData.twilioAuthToken.trim();

  const handleConnect = () => {
    if (!validateForm()) return;

    // Call the Express API which creates in VAPI first, then persists to DB
    connectPhone.mutate(
      {
        agentId: voiceAgent.id,
        phoneNumber: formData.phoneNumber,
        twilioAccountSid: formData.twilioAccountSid,
        twilioAuthToken: formData.twilioAuthToken,
      },
      {
        onSuccess: (updatedAgent) => {
          onSuccess(updatedAgent);
        },
      },
    );
  };

  // If already connected to Vapi, show status
  if (
    voiceAgent.phone_number &&
    voiceAgent.twilio_account_sid &&
    voiceAgent.vapi_phone_number_id
  ) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-500" />
            <div>
              <p className="font-medium text-green-900">Connected</p>
              <p className="text-lg font-mono text-green-700">
                {voiceAgent.phone_number}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Show reconnect info banner if in reconnect scenario */}
      {isReconnectScenario && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-amber-900 mb-1">
                Phone Number Disconnected
              </h4>
              <p className="text-sm text-amber-700">
                Your phone number <strong>{voiceAgent.phone_number}</strong> was
                previously connected but is now disconnected from the voice
                system. Click "Reconnect Phone Number" below to restore the
                connection using your saved credentials.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Information Cards */}
      <Accordion type="multiple" className="w-full space-y-3">
        <AccordionItem
          value="what-is-twilio"
          className="bg-blue-50 rounded-2xl border border-blue-200 px-4"
        >
          <AccordionTrigger className="text-left hover:no-underline py-4 text-base font-medium text-blue-900 hover:text-blue-700">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              What is Twilio?
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 text-blue-800 leading-relaxed">
            <div className="space-y-3">
              <p>
                Twilio is the most popular cloud phone provider in the world.
                You need a Twilio account and phone number for each Hubflo AI
                agent.
              </p>
              <p>
                <strong>Pricing:</strong> The Twilio cost is not included in
                your Hubflo subscription but it starts as low as{" "}
                <strong>$0.0085/min</strong> for incoming calls.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <a
                  href="https://www.twilio.com/try-twilio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 font-medium hover:text-blue-800 underline hover:no-underline"
                >
                  Create a free account here
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <p className="text-sm">
                Get your credentials from the{" "}
                <a
                  href="https://console.twilio.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:no-underline font-medium"
                >
                  Twilio Console
                </a>
                .
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="non-twilio-number"
          className="bg-amber-50 rounded-2xl border border-amber-200 px-4"
        >
          <AccordionTrigger className="text-left hover:no-underline py-4 text-base font-medium text-amber-900 hover:text-amber-700">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              How to connect a non-Twilio number?
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4 text-amber-800 leading-relaxed">
            <div className="space-y-3">
              <p>
                While a Twilio number is required, most providers allow you to
                setup a <strong>forwarding rule</strong> to an external number.
              </p>
              <p>
                This is a pretty good solution so you can{" "}
                <strong>keep your current phone number</strong> and simply set
                it to auto forward to your Hubflo AI agent Twilio phone number.
              </p>
              <p>You can even set advanced rules, for example:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Forward to the AI agent only outside business hours</li>
                <li>Forward only if you don't pick up after a few rings</li>
                <li>Forward specific types of calls</li>
              </ul>
              <p className="text-sm font-medium">
                Check with your phone provider on how to configure call
                forwarding rules.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="phoneNumber">Phone Number</Label>
          <Input
            id="phoneNumber"
            placeholder="+1234567890"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
            className={errors.phoneNumber ? "border-red-500" : ""}
          />
          {errors.phoneNumber && (
            <p className="text-sm text-red-600">{errors.phoneNumber}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Your Twilio phone number in E.164 format (e.g., +1234567890)
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="twilioAccountSid">Account SID</Label>
          <Input
            id="twilioAccountSid"
            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            value={formData.twilioAccountSid}
            onChange={(e) =>
              handleInputChange("twilioAccountSid", e.target.value)
            }
            className={errors.twilioAccountSid ? "border-red-500" : ""}
          />
          {errors.twilioAccountSid && (
            <p className="text-sm text-red-600">{errors.twilioAccountSid}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Found in your Twilio Console (starts with "AC")
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="twilioAuthToken">Auth Token</Label>
          <Input
            id="twilioAuthToken"
            type="password"
            placeholder="Your Twilio Auth Token"
            value={formData.twilioAuthToken}
            onChange={(e) =>
              handleInputChange("twilioAuthToken", e.target.value)
            }
            className={errors.twilioAuthToken ? "border-red-500" : ""}
          />
          {errors.twilioAuthToken && (
            <p className="text-sm text-red-600">{errors.twilioAuthToken}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Your secret token from Twilio Console
          </p>
        </div>
      </div>

      {isReconnectScenario ? (
        // Reconnect button - credentials are pre-filled
        <Button
          onClick={handleConnect}
          disabled={isLoading || !allFieldsFilled}
          className="w-full"
          size="lg"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Reconnecting...
            </>
          ) : (
            <>
              <Phone className="mr-2 h-4 w-4" />
              Reconnect Phone Number
            </>
          )}
        </Button>
      ) : (
        // Regular connect button for first-time setup
        <Button
          onClick={handleConnect}
          disabled={isLoading || !allFieldsFilled}
          className="w-full"
          variant={allFieldsFilled ? "default" : "secondary"}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Connecting...
            </>
          ) : (
            "Connect Phone Number"
          )}
        </Button>
      )}
    </div>
  );
}
